'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface RoyaltyRow {
  id: string;
  dsp: string;
  territory: string;
  streams: number;
  earnings_inr: number;
  earnings_usd: number;
  period_start: string;
  period_end: string;
  releases?: { title: string; release_type: string } | null;
}

const DSP_COLORS: Record<string, string> = {
  'Spotify': '#1db954',
  'Apple Music': '#fc3c44',
  'YouTube Music': '#ff0000',
  'Amazon Music': '#00a8e1',
  'Tidal': '#00ffff',
};

const DSP_LIST = ['Spotify', 'Apple Music', 'YouTube Music', 'Amazon Music', 'Tidal'];

function formatInr(val: number) {
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function RoyaltiesPage() {
  const [tab, setTab] = useState<'overview' | 'by-release' | 'by-dsp' | 'by-territory'>('overview');
  const [royalties, setRoyalties] = useState<RoyaltyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchRoyalties() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/royalties/stats');
        if (!res.ok) throw new Error('Failed to load royalties');
        const json = await res.json();
        setRoyalties(json.royalties || []);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchRoyalties();
  }, []);

  const totalInr = royalties.reduce((s, r) => s + Number(r.earnings_inr || 0), 0);
  const totalStreams = royalties.reduce((s, r) => s + Number(r.streams || 0), 0);

  // By DSP aggregation
  const byDsp = DSP_LIST.map((dsp) => {
    const rows = royalties.filter((r) => r.dsp === dsp);
    return {
      dsp,
      streams: rows.reduce((s, r) => s + Number(r.streams || 0), 0),
      earningsInr: rows.reduce((s, r) => s + Number(r.earnings_inr || 0), 0),
      color: DSP_COLORS[dsp] || '#888',
    };
  }).filter((d) => d.earningsInr > 0);

  // By Release aggregation
  const byRelease: Record<string, { title: string; earningsInr: number; streams: number }> = {};
  royalties.forEach((r) => {
    const key = r.releases?.title || 'Unknown';
    if (!byRelease[key]) byRelease[key] = { title: key, earningsInr: 0, streams: 0 };
    byRelease[key].earningsInr += Number(r.earnings_inr || 0);
    byRelease[key].streams += Number(r.streams || 0);
  });

  // By Territory aggregation
  const byTerritory: Record<string, number> = {};
  royalties.forEach((r) => {
    const t = r.territory || 'GLOBAL';
    byTerritory[t] = (byTerritory[t] || 0) + Number(r.earnings_inr || 0);
  });

  function handleExportCSV() {
    const headers = ['DSP', 'Territory', 'Streams', 'Earnings (INR)', 'Earnings (USD)', 'Period Start', 'Period End', 'Release'];
    const rows = royalties.map((r) => [
      r.dsp, r.territory, r.streams, r.earnings_inr, r.earnings_usd, r.period_start, r.period_end, r.releases?.title || '',
    ]);
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tubenexa_royalties_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'by-release', label: 'By Release' },
    { id: 'by-dsp', label: 'By DSP' },
    { id: 'by-territory', label: 'By Territory' },
  ] as const;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Royalties</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Earnings breakdown by release, DSP, and territory</p>
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border"
            style={{ background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
          >
            <Icon name="ArrowDownTrayIcon" size={16} />
            Export CSV
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'Total Earnings', value: formatInr(totalInr), icon: 'BanknotesIcon', color: 'var(--warning)', bg: 'var(--warning-bg)' },
            { label: 'Total Streams', value: totalStreams.toLocaleString('en-IN'), icon: 'ChartBarIcon', color: 'var(--primary-light)', bg: 'var(--purple-bg)' },
            { label: 'Active DSPs', value: byDsp.length.toString(), icon: 'GlobeAltIcon', color: 'var(--success)', bg: 'var(--success-bg)' },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: kpi.bg }}>
                  <Icon name={kpi.icon as any} size={20} style={{ color: kpi.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold font-mono-data" style={{ color: 'var(--foreground)' }}>{kpi.value}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: tab === t.id ? 'var(--primary)' : 'transparent',
                color: tab === t.id ? 'white' : 'var(--muted-foreground)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
              <span className="ml-3 text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading royalties...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="ExclamationCircleIcon" size={32} className="mx-auto mb-3" style={{ color: 'var(--danger)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--danger)' }}>{error}</p>
            </div>
          ) : royalties.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="BanknotesIcon" size={40} className="mx-auto mb-4" style={{ color: 'var(--muted-foreground)' }} />
              <p className="text-base font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No royalty data yet</p>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Royalties appear once your releases go live on DSPs.</p>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {tab === 'overview' && (
                <div className="space-y-4">
                  <h3 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Earnings Timeline</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          {['DSP', 'Release', 'Territory', 'Streams', 'Earnings (INR)', 'Period'].map((h) => (
                            <th key={h} className="text-left pb-3 pr-4 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {royalties.map((r) => (
                          <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ background: DSP_COLORS[r.dsp] || '#888' }} />
                                <span style={{ color: 'var(--foreground)' }}>{r.dsp}</span>
                              </div>
                            </td>
                            <td className="py-3 pr-4 text-xs" style={{ color: 'var(--muted-foreground)' }}>{r.releases?.title || '—'}</td>
                            <td className="py-3 pr-4 text-xs" style={{ color: 'var(--muted-foreground)' }}>{r.territory}</td>
                            <td className="py-3 pr-4 font-mono-data text-xs" style={{ color: 'var(--foreground)' }}>{Number(r.streams).toLocaleString('en-IN')}</td>
                            <td className="py-3 pr-4 font-mono-data text-xs font-semibold" style={{ color: 'var(--warning)' }}>{formatInr(Number(r.earnings_inr))}</td>
                            <td className="py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{r.period_start} → {r.period_end}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* By Release Tab */}
              {tab === 'by-release' && (
                <div className="space-y-3">
                  <h3 className="text-base font-bold mb-4" style={{ color: 'var(--foreground)' }}>Earnings by Release</h3>
                  {Object.values(byRelease).map((rel) => (
                    <div key={rel.title} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{rel.title}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{Number(rel.streams).toLocaleString('en-IN')} streams</p>
                      </div>
                      <p className="text-base font-bold font-mono-data" style={{ color: 'var(--warning)' }}>{formatInr(rel.earningsInr)}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* By DSP Tab */}
              {tab === 'by-dsp' && (
                <div className="space-y-4">
                  <h3 className="text-base font-bold mb-4" style={{ color: 'var(--foreground)' }}>Earnings by DSP</h3>
                  {byDsp.map((d) => (
                    <div key={d.dsp} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                          <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{d.dsp}</span>
                        </div>
                        <span className="text-sm font-bold font-mono-data" style={{ color: 'var(--warning)' }}>{formatInr(d.earningsInr)}</span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                        <div className="h-full rounded-full" style={{ width: `${Math.round((d.earningsInr / totalInr) * 100)}%`, background: d.color }} />
                      </div>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{Number(d.streams).toLocaleString('en-IN')} streams · {Math.round((d.earningsInr / totalInr) * 100)}%</p>
                    </div>
                  ))}
                </div>
              )}

              {/* By Territory Tab */}
              {tab === 'by-territory' && (
                <div className="space-y-3">
                  <h3 className="text-base font-bold mb-4" style={{ color: 'var(--foreground)' }}>Earnings by Territory</h3>
                  {Object.entries(byTerritory).map(([territory, amount]) => (
                    <div key={territory} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3">
                        <Icon name="GlobeAltIcon" size={18} style={{ color: 'var(--primary-light)' }} />
                        <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{territory}</span>
                      </div>
                      <p className="text-base font-bold font-mono-data" style={{ color: 'var(--warning)' }}>{formatInr(amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
